import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static BALANCE_TOLERANCE = 0.005

  static targets = [
    "lines",
    "line",
    "template",
    "balance",
    "amount",
    "submit",
    "accountSelect",
    "costCentreSelect",
    "status",
    "autoBalanceButton"
  ]

  connect() {
    if (this.linesTarget.children.length === 0) {
      this.addLine();
    }

    this.updateBalance()
  }

  amountChanged(event) {
    const line = event.target.closest(".split-line")

    if (this.isAutoBalanceEnabled(line)) {
      this.disableAutoBalance(line)
    }

    this.updateBalance()
  }

  toggleAutoBalance(event) {
    event.preventDefault()

    const lastLine = this.lineTargets[this.lineTargets.length - 1]
    if (!lastLine) return

    const isEnabled = this.isAutoBalanceEnabled(lastLine)

    if (isEnabled) {
      this.disableAutoBalance(lastLine)
    } else {
      this.enableAutoBalance(lastLine)
    }

    this.updateBalance()
    // this.updateAutoBalanceButton()
  }

  addLine(event) {
    if (event) event.preventDefault()

    const keepAutoBalanceEnabled = Boolean(this.autoBalanceLine())
    const template = this.templateTarget.innerHTML
    const uniqueId = Date.now() // or better random unique string

    // Replace new_RECORD placeholder keys with unique key
    const newLineHtml = template.replace(/new_RECORD/g, `new_${uniqueId}`)

    this.linesTarget.insertAdjacentHTML("beforeend", newLineHtml)
    const newLastLine = this.linesTarget.lastElementChild

    if (keepAutoBalanceEnabled && newLastLine) {
      this.enableAutoBalance(newLastLine)
    }

    this.updateBalance()
  }

  lineAdded(event) {
    const newLine = event.detail?.line
    const lastLine = this.lineTargets[this.lineTargets.length - 1]

    if (newLine === lastLine && this.autoBalanceLine()) {
      this.enableAutoBalance(newLine)
    }

    this.updateBalance()
  }


  removeLine(event) {
    event.preventDefault()
    const line = event.target.closest(".split-line")
    if (line) {
      const keepAutoBalanceEnabled = this.isAutoBalanceEnabled(line)
      line.remove()

      if (this.lineTargets.length === 0) {
        this.addLine()
      }

      if (keepAutoBalanceEnabled) {
        const newLastLine = this.lineTargets[this.lineTargets.length - 1]
        if (newLastLine) {
          this.enableAutoBalance(newLastLine)
        }
      }

      this.updateBalance()
    }
  }

  setAutoBalanceState(line, enabled) {
    const input = this.amountInputForLine(line)
    if (!input) return

    input.classList.toggle("auto-balanced", enabled)
  }

  amountInputForLine(line) {
    return line?.querySelector('input[name*="amount"]')
  }

  autoBalanceLine() {
    return this.lineTargets.find(
      line => this.isAutoBalanceEnabled(line)
    )
  }

  autoBalanceAmountInput() {
    const line = this.autoBalanceLine()
    return this.amountInputForLine(line)
  }

  calculateTotal(excludingLine = null) {
    let total = 0.0

    this.lineTargets.forEach((line) => {
      if (line === excludingLine) return

      const amountInput = this.amountInputForLine(line)
      if (!amountInput) return

      const value = parseFloat(amountInput.value)
      if (!isNaN(value)) {
        total += value
      }
    })

    return total
  }

  applyAutoBalance() {
    const line = this.autoBalanceLine()
    if (!line) return

    const input = this.amountInputForLine(line)
    if (!input) return

    const totalOfOthers = this.calculateTotal(line)
    const expected = this.expectedAmount()
    const remainder = expected - totalOfOthers

    const current = parseFloat(input.value)

    // Blank and otherwise invalid inputs must be populated on first use.
    const shouldWriteValue =
      Number.isNaN(current) ||
      Math.abs(current - remainder) >= this.constructor.BALANCE_TOLERANCE

    if (shouldWriteValue) {
      input.value = remainder.toFixed(2)
      line.dispatchEvent(new CustomEvent("categorisation:changed", { bubbles: true }))
    }

    // ALWAYS enforce visual state
    this.setAutoBalanceState(line, true)
  }

  enableAutoBalance(line) {
    // disable any existing ones first
    this.lineTargets.forEach((l) => {
      if (this.isAutoBalanceEnabled(l)) {
        this.disableAutoBalance(l)
      }
    })

    line.dataset.autoBalance = "true"
    this.setAutoBalanceState(line, true)
    this.applyAutoBalance()
    this.updateAutoBalanceButton()
  }

  disableAutoBalance(line) {
    line.dataset.autoBalance = "false"
    this.setAutoBalanceState(line, false)
    this.updateAutoBalanceButton()
  }

  isAutoBalanceEnabled(line) {
    return line?.dataset.autoBalance === "true"
  }

  updateAutoBalanceButton() {
    const lastLine = this.lineTargets[this.lineTargets.length - 1]
    const enabled = this.isAutoBalanceEnabled(lastLine)

    if (!this.hasAutoBalanceButtonTarget) return

    this.autoBalanceButtonTarget.innerHTML = enabled
      ? '<i class="fa-solid fa-scale-unbalanced"></i> Stop auto-balancing'
      : '<i class="fa-solid fa-scale-balanced"></i> Auto-balance last line'
  }

  clearAutoBalanceState() {
    this.lineTargets.forEach(line => {
      line.dataset.autoBalance = "false"
      this.setAutoBalanceState(line, false)
    })
  }

  updateTransferCategories() {
    const multipleLines = this.lineTargets.length > 1
    const templateGroups = Array.from(this.templateTarget.content.querySelectorAll("optgroup"))
      .filter(group => group.querySelector('option[data-external="true"]'))

    this.accountSelectTargets.forEach(select => {
      const selectedTransfer = select.selectedOptions[0]?.dataset.external === "true"
      const groups = Array.from(select.querySelectorAll("optgroup"))
        .filter(group => group.querySelector('option[data-external="true"]'))

      if (multipleLines) {
        if (selectedTransfer) select.value = "select"
        groups.forEach(group => group.remove())
        if (selectedTransfer) {
          this.transferSelectionCleared = true
          select.dispatchEvent(new Event("change", { bubbles: true }))
        }
      } else if (groups.length === 0) {
        templateGroups.forEach(group => {
          const followingLabel = group.nextElementSibling?.label
          const followingGroup = Array.from(select.children).find(child => child.label === followingLabel)
          select.insertBefore(group.cloneNode(true), followingGroup || null)
        })
      }
    })

    if (!multipleLines || this.accountSelectTargets.every(select => select.value && select.value !== "select")) {
      this.transferSelectionCleared = false
    }
  }

  updateBalance() {
    this.updateTransferCategories()
    // console.log('updateBalance')

    const autoLine = this.autoBalanceLine()

    // STEP 1: validate auto-balance integrity
    if (autoLine && !this.lineTargets.includes(autoLine)) {
      this.clearAutoBalanceState()
    }

    this.applyAutoBalance()

    const errors = []
    const warnings = []

    let total = this.calculateTotal()
    let allAmountsValid = this.amountTargets.length > 0
    let allAccountsSelected = this.accountSelectTargets.length > 0
    let allCostCentresSelected = this.costCentreSelectTargets.length > 0
    let allAccountsValid = true

    this.amountTargets.forEach((input) => {
      const val = parseFloat(input.value)
      if (!input.value || isNaN(val) || val === 0) {
        input.classList.add('invalid-amount');
        if (allAmountsValid) {
          errors.push("Some amounts are not valid.")
          allAmountsValid = false;
        }
      } else {
        input.classList.remove('invalid-amount');
        // total += val;
      }
    })

    // Check account selects
    this.accountSelectTargets.forEach((select) => {
      if (!select.value || select.value === "select") {
        if (allAccountsSelected) {
          errors.push("Not all categories are selected.")
          allAccountsSelected = false;
        }
      }
    });

    // Check cost centre selects
    this.costCentreSelectTargets.forEach((select) => {
      const accountSelect = select.closest(".split-line")?.querySelector('[data-split-target="accountSelect"]')
      if (accountSelect?.selectedOptions[0]?.dataset.external === "true") return

      if (select.value === "select") {
        if (allCostCentresSelected) {
          errors.push("Not all properties are selected.")
          allCostCentresSelected = false;
        }
      }
    });

    const difference = this.expectedAmount() - total

    const balanced = Math.abs(difference) < this.constructor.BALANCE_TOLERANCE

    const display = balanced
      ? "0.00"
      : difference.toFixed(2)


    this.balanceTarget.textContent = `Difference: £${display}`
    this.balanceTarget.classList.toggle("text-danger", !balanced)
    this.balanceTarget.classList.toggle("text-success", balanced)

    if (!balanced) {
      errors.push("The amounts must add up to the transaction total.")
    }

    let unusualSigns = false

    this.lineTargets.forEach((line) => {
      const amountInput = this.amountInputForLine(line);
      const accountSelect = line.querySelector('select[name*="account_id"]');
      const costCentreSelect = line.querySelector('select[name*="cost_centre"]');

      // Remove previous styling
      amountInput.classList.remove('unusual-sign');

      if (accountSelect && accountSelect.value !== 'select') {
        const normalSign = parseInt(accountSelect.selectedOptions[0]?.dataset.normal, 10);
        const amount = parseFloat(amountInput.value);

        // data-normal is the *normal* sign, but we have to look at it from POV of the external account
        if (accountSelect.selectedOptions[0]?.dataset.external !== "true" && !isNaN(amount) && normalSign && amount * normalSign > 0) {
          // Highlight if sign is unusual
          amountInput.classList.add('unusual-sign');
          unusualSigns = true;
        }

        const isArchived = accountSelect.selectedOptions[0]?.dataset.archived === 'true'
        if (isArchived) {
          allAccountsValid = false;
        }
      }
    })

    if (this.transferSelectionCleared) {
      warnings.push("Transfer category cleared. Transfers are only available when there is one line.")
    }

    if (!allAccountsValid) {
      warnings.push("Some categories are archived.")
    }

    if (unusualSigns) {
      warnings.push("There are some unusual amounts, but they may be correct, please check. For example, expenses are usually negative.")
    }

    this.updateAutoBalanceButton()

    this.submitTarget.disabled = !(balanced && allAmountsValid && allAccountsSelected && allAccountsValid && allCostCentresSelected)

    this.statusTarget.innerHTML = warnings.concat(errors).map(msg => `• ${msg}`).join("<br/>")
  }

  expectedAmount() {
    const value = parseFloat(this.element.dataset.splitExpectedAmount)
    // console.log("expectedAmount is " + value)
    return isNaN(value) ? 0.0 : value
  }

  formatAmount(event) {
    const field = event.target;
    const value = parseFloat(field.value);
    if (!isNaN(value)) {
      field.value = value.toFixed(2);
    }
  }

}
